import React, { ChangeEvent, useEffect } from 'react';
import TopFormSearch, { TopFormSearchOption } from './TopFormSearch';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Divider,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Container,
  Button
} from '@material-ui/core';
import DateRangePicker from './date-range-picker.component';
import Dealership from '../_models/Dealership';
import AutocompleteSelect, { AutocompleteOption } from './common/AutocompleteSelect';
import RadioFormSearch from './RadioForm';
import { RadioSearchOptions } from './RadioForm';
import { API } from 'aws-amplify';
import { getAPIName } from '../config';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexWrap: 'wrap'
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: 200
    },
    dense: {
      marginTop: theme.spacing(2)
    },
    menu: {
      width: 200
    },
    gapBetweenSearchBtn:{
      marginBottom:'10px'
    }
  })
);

export interface TopFormState {
  readonly dealershipId: number;
  readonly endDate: Date | null;
  readonly searchInput: string;
  readonly searchType: TopFormSearchOption;
  readonly startDate: Date | null;
  readonly statusSelection?: StatusOption;
  readonly authTokenStatusSelection?: StatusOption;
  readonly timeSearchType?: RadioSearchOptions;
}

export interface StatusOption {
  readonly name: string;
  readonly value: string;
}

export type GridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface GridLayout {
  readonly xs?: GridSize;
  readonly md?: GridSize;
}

export interface TopFormGridConfig {
  readonly location?: GridLayout;
  readonly status?: GridLayout;
  readonly authStatus?: GridLayout;
  readonly search?: GridLayout;
  readonly date?: GridLayout;
}

export interface TopFormProperties {
  readonly onChange: (state: TopFormState) => void;
  readonly onSearch: () => void;
  readonly searchOptions: TopFormSearchOption[];
  readonly state: TopFormState;
  readonly statusOptions: StatusOption[];
  readonly authTokenStatusOptions?: StatusOption[];
  readonly timestampTypeSearchOption? : RadioSearchOptions[];
  readonly numericFields? : string[];
  readonly numericRangeFields?: string[];
  readonly gridConfig?: TopFormGridConfig;
}

export default function TopForm(props: TopFormProperties) {
  const classes = useStyles();

  // Default grid configuration for backward compatibility
  const defaultGridConfig: TopFormGridConfig = {
    location: { xs: 6, md: 3 },
    status: { xs: 6, md: 3 },
    authStatus: { xs: 6, md: 3 },
    search: { xs: 6, md: 3 },
    date: { xs: 6, md: 3 }
  };

  // Merge user config with defaults
  const gridConfig: TopFormGridConfig = {
    ...defaultGridConfig,
    ...props.gridConfig
  };

  // handlers /////////////////////////////////////////////////////////////////
  const handleTimeSearchInputChange = (searchInput: RadioSearchOptions) =>{
    props.onChange({
      ...props.state,
      timeSearchType:searchInput
    })
  }

  const handleStartDateChange = (date: Date | null) => {
    props.onChange({
      ...props.state,
      startDate: date
    });
  };
  const handleEndDateChange = (date: Date | null) => {
    props.onChange({
      ...props.state,
      endDate: date
    });
  };
  const handleDealershipDropdown = (id: number | null) => {
    props.onChange({
      ...props.state,
      dealershipId: id || 0
    });
  };
  const handleStatusDropdown = (value: any) => {
    props.onChange({
      ...props.state,
      statusSelection: value ? props.statusOptions.find(so => so.value === value) : undefined,
    });
  };
  const handleAuthTokenStatusDropdown = (value: any) => {
    let authTokenStatusSelection = !value || !props.authTokenStatusOptions
      ? undefined
      : props.authTokenStatusOptions.find(so => so.value === value);

    props.onChange({
      ...props.state,
      authTokenStatusSelection: authTokenStatusSelection
    });
  };
  const handleSearchInputChange = (searchInput: string): void => {
    props.onChange({
      ...props.state,
      searchInput: ['VIN', 'License Plate'].includes(props.state.searchType.label) ? searchInput.toUpperCase() : searchInput
    });
  };

  const handleSearchTypeChange = (searchType: TopFormSearchOption): void => {
    const isNumeric = props.numericFields?.includes(searchType.value) || false;
    const isNumericRange = props.numericRangeFields?.includes(searchType.value) || false;
    const hasValidInput = isNumericRange
      ? /^\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?$/.test(props.state.searchInput)
      : /^\d+$/.test(props.state.searchInput);
    props.onChange({
      ...props.state,
      searchType,
      searchInput:
        (isNumeric || isNumericRange) &&
        props.state.searchInput &&
        !hasValidInput
          ? ''
          : props.state.searchInput
    });
  };

  // JSX.Element preprocessing /////////////////////////////////////////////////
  const statusOptions: AutocompleteOption[] = props.statusOptions.map(o => ({
    label: o.name,
    value: o.value
  }));

  const authTokenStatusOptions: AutocompleteOption[] = props.authTokenStatusOptions?.map(o => ({
    label: o.name,
    value: o.value
  })) || [];
  

  return (
    <div className="TopForm">
      <Divider />
      <Grid container spacing={2}>
        <Grid item xs={gridConfig.location?.xs} md={gridConfig.location?.md}>
          <AutocompleteSelect
            apiPath="/dealerships"
            keySelector="id"
            valueSelector="name"
            value={props.state.dealershipId || undefined}
            onChange={handleDealershipDropdown}
            placeholder="Select Location"
            label="Location"
            flags={{
              useAPIData: true,
              enableAutocomplete: true,
              sortOptions: true,
            }}
          />
        </Grid>
        <Grid item xs={gridConfig.status?.xs} md={gridConfig.status?.md}>
          <AutocompleteSelect
            options={statusOptions}
            value={props.state.statusSelection?.value}
            onChange={handleStatusDropdown}
            label="Status"
            placeholder="Select Status"
            flags={{
              enableAutocomplete: true,
              sortOptions: true,
            }}
          />
        </Grid>
        {props.authTokenStatusOptions && authTokenStatusOptions.length > 0 && 
        <Grid item xs={gridConfig.authStatus?.xs} md={gridConfig.authStatus?.md}>
          <AutocompleteSelect
            options={authTokenStatusOptions}
            value={props.state.authTokenStatusSelection?.value}
            onChange={handleAuthTokenStatusDropdown}
            label="Auth Status"
            placeholder="Select Auth Status"
            flags={{
              enableAutocomplete: true,
              sortOptions: true,
            }}
          />
        </Grid>
        }
        <Grid item xs={gridConfig.search?.xs} md={gridConfig.search?.md}>
          <TopFormSearch
            onSearchInputChange={handleSearchInputChange}
            onSearchTypeChange={handleSearchTypeChange}
            searchInput={props.state.searchInput}
            searchOptions={props.searchOptions}
            numericOnlyFields={props.numericFields}
            numericRangeFields={props.numericRangeFields}
          />
        </Grid>

        <Grid item xs={gridConfig.date?.xs} md={gridConfig.date?.md}>
          <DateRangePicker
            endDate={props.state.endDate || null}
            handleEndDateChange={handleEndDateChange}
            handleStartDateChange={handleStartDateChange}
            pickTime={true}
            startDate={props.state.startDate || null}
          />
          {
            props.timestampTypeSearchOption && 
            <RadioFormSearch
              onSearchTypeChange={handleTimeSearchInputChange}
              radioSearchOptions={props.timestampTypeSearchOption}
            />
          }
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={props.onSearch} className={classes.gapBetweenSearchBtn}>
            Search
          </Button>
        </Grid>

      </Grid>
    </div>
  );
}
